import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import type { TPublishStatus } from 'components/StackedChart/types'
import type { TimelineDataLabelsFormatterContextObject } from 'highcharts'
import type { TRGBAColorWith1AtTheEnd } from 'settings/colors'
import { colors, irrigationEventsColorMap } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { getIrrigationEventColor } from './getIrrigationEventColor'

export const makeEmitterSeries = (
  emitterData: {
    [irrigationZoneEmitterId: TIrrigationZoneEmitterId]: {
      emitterType: TEmitterType
      zoneId: number
      startTime: string
      endTime: string
      publishStatus?: TPublishStatus
    }[]
  },
  hasRain: boolean,
  showDurationLabels?: boolean,
) => {
  const properties = fieldAssetStore.getState().properties
  const selectedProperty = selectedFieldAssetsStore.getState().property

  const irrigationZoneEmittersForProperty =
    (selectedProperty && properties?.[selectedProperty]?.irrigationZoneEmitters) || {}

  return Object.entries(emitterData)
    .sort(([a], [b]) => {
      const emitterAZoneName = irrigationZoneEmittersForProperty?.[a as TIrrigationZoneEmitterId]?.name
      const emitterBZoneName = irrigationZoneEmittersForProperty?.[b as TIrrigationZoneEmitterId]?.name

      // reverse order because the chart order starts from the bottom
      return alphabeticalSort(emitterBZoneName, emitterAZoneName)
    })
    .sort(([a], [b]) => {
      const emitterA = getIdAndEmitterTypeFromZoneEmitterTypeKey(a as TIrrigationZoneEmitterId).emitterType
      const emitterB = getIdAndEmitterTypeFromZoneEmitterTypeKey(b as TIrrigationZoneEmitterId).emitterType

      // reverse order because the chart order starts from the bottom
      return alphabeticalSort(emitterB, emitterA)
    })
    .map(([irrigationZoneEmitterId, emitterEvents], emitterIndex: number) => {
      const emitterType = irrigationZoneEmitterId.split('|||')[1] as TEmitterType
      const color = irrigationEventsColorMap[emitterType]

      const intervalsForChart = emitterEvents.map((s, index) => {
        const isFuture = s.startTime > new Date().toISOString()

        return {
          key: `${index}-${irrigationZoneEmitterId}-${emitterIndex}`,
          x: +new Date(s.startTime),
          x2: s.endTime ? +new Date(s.endTime) : undefined,
          y: hasRain ? emitterIndex + 1 : emitterIndex,
          color: getIrrigationEventColor(color, isFuture, s.publishStatus) as TRGBAColorWith1AtTheEnd,
          pointWidth: 14,
          publishStatus: s.publishStatus,
        }
      })

      return {
        type: 'xrange' as const,
        showInLegend: false,
        data: intervalsForChart,
        id: `${irrigationZoneEmitterId}-${emitterIndex}`,
        irrigationZoneEmitterId: irrigationZoneEmitterId as TIrrigationZoneEmitterId,
        zoneName:
          irrigationZoneEmitterId &&
          irrigationZoneEmittersForProperty?.[irrigationZoneEmitterId as TIrrigationZoneEmitterId]?.name,
        pointPadding: 0.5,
        groupPadding: 0.5,
        borderRadius: 2,
        borderColor: colors.grey800,
        color,
        name: mapApiEmitterTypeToLabel(irrigationZoneEmitterId.split('|||')[1] as TEmitterType),
        ...(showDurationLabels && {
          dataLabels: {
            enabled: true,
            formatter: function (this: TimelineDataLabelsFormatterContextObject): string {
              const durationMinutes =
                typeof this.point.x === 'number' && typeof this.point.x2 === 'number'
                  ? (this.point.x2 - this.point.x) / (1000 * 60)
                  : 0

              return durationMinutes >= 60 ? minutesToHoursAndMinutes(durationMinutes) : ''
            },
            style: {
              color: 'white',
              fontSize: '9px',
            },
          },
        }),
      }
    })
}
